import _ from "lodash";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSelector, useDispatch } from "react-redux";

import { setDisableBtn, toggleDeleteDialog, toggleDialog } from "../redux/Slices/Global/globalSlice";
import { handleGroupsData, handleResErrors } from "./utils";
import { useCustomToast } from "../pages/Contexts/ToastContext";
import axiosInstance from "../config/Axios";
import { resetState, setCompanyGroups, setEditRotation, setSelectedCampaign, setSelectedDevice } from "../redux/Slices/deviceSlice";

// -------------------------Get Grouped Devices-------------------------//
const getGroupedDevices = async ({ queryKey }) => {
    const [, filteredDeviceFilters] = queryKey;
    const { data } = await axiosInstance.get(`/groups/company`, { params: filteredDeviceFilters });
    return handleGroupsData(data);
}

export const useGetGroupedDevices = () => {
    const { deviceFilters } = useSelector((state) => state.device);
    const { showToast } = useCustomToast();
    const dispatch = useDispatch();
    const filteredDeviceFilters = _.pickBy(deviceFilters, value => value !== null && value !== '');

    let { isLoading, data, error, isError, isFetching, refetch } = useQuery(
        ['getGroupedDevices', filteredDeviceFilters],
        getGroupedDevices,
        {
            keepPreviousData: true,
            // Poll so the backend `online` flag stays fresh — this is the
            // fallback source when the Firebase RTDB listener can't reach a
            // device (e.g. 4G/mobile WebSocket blocked). Backend marks a device
            // offline after 11s without a heartbeat, so 15s keeps the Screens
            // page roughly in sync. refetchIntervalInBackground stays false
            // (default) so we don't poll while the tab is hidden.
            refetchInterval: 15000,
            onSuccess: (data) => {
                let _options = []
                Object.values(data).forEach(group => {
                    _options = [..._options, {
                        name: group.name,
                        id: group.group_id
                    }]
                })
                dispatch(setCompanyGroups(_options))
                dispatch(setDisableBtn(false))
            },
        }
    );

    if (isError) {
        showToast("error", "Fetching Data", error.response?.data?.message)
    }

    // isError added (2026-09-16): computed above for the toast but never
    // exposed — the Screens page's new error/retry state (replacing the
    // old toast-only behavior when there's nothing on screen yet) needs it.
    // Purely additive; only consumer is Devices/Index.jsx.
    return { isLoading, data, isFetching, refetch, isError };
}

// -------------------------Get Devices List-------------------------//
const getDevicesList = async ({ queryKey }) => {
    const [, pageNum, searchTerm] = queryKey;
    let url = `/company/devices${!_.isNull(pageNum) ? `?page=${pageNum}&per_page=7` : ""}`;

    if (searchTerm) {
        url += `${!_.isNull(pageNum) ? '&' : '?'}search=${searchTerm}`;
    }
    const { data } = await axiosInstance.get(url);

    return data;

}

export const useGetDevicesList = (pageNum = null, searchTerm = '', enabled = true) => {
    const { showToast } = useCustomToast();

    let { isLoading: devicesLoading, data, isFetching, refetch: refetchDevices } = useQuery(
        ['getDevicesList', pageNum, searchTerm],
        getDevicesList,
        {
            enabled,
            refetchOnMount: true,
            onError: (error) => showToast("error", "Fetching Data", error.response?.data?.message)

        }
    );

    return { devicesLoading, data, refetchDevices, isFetching };
}

// -------------------------All Company Devices (flat list, for dropdowns)-------//
// GET /api/company/devices/all → [{ id, name, serial_name }]. Non-paginated,
// scoped to the auth'd user's company, excludes EXPIRED devices. Used by the
// Receivers form to pick a Connected Player.
export const useGetAllCompanyDevices = (enabled = true) => {
    const { showToast } = useCustomToast();
    return useQuery(
        'allCompanyDevices',
        async () => (await axiosInstance.get('/company/devices/all')).data,
        {
            enabled,
            staleTime: 60_000,
            onError: (err) => showToast('error', 'Devices', err.response?.data?.message),
        }
    );
};

// -------------------------Get Countries-------------------------//
const getCountries = async () => {
    const { data } = await axiosInstance.get("/countries/all",);

    // Resilient to a missing/empty geonames payload or Laravel resource wrapping
    // ({ data: { geonames } }) — otherwise .map on undefined throws and spams the
    // "Fetching Data" toast every render (seen when opening the device edit dialog).
    const list = data?.geonames ?? data?.data?.geonames ?? [];

    return list.map(country => ({
        label: country.countryName,
        value: country.countryCode,
    }));
}

export const useGetCountries = () => {
    const { showToast } = useCustomToast();

    let { isLoading, data, error, isError } = useQuery('getCountries', getCountries);

    if (isError) {
        showToast("error", "Fetching Data", error.response?.data?.message)
    }

    return { isLoading, data };
}

// -------------------------Get Cites-------------------------//
const getCites = async ({ queryKey }) => {
    const { data } = await axiosInstance.get(`/countries/${queryKey[1].countryName}/cities/all`);

    const _cities = data?.geonames.filter(city => city.adminName1 != "")
        .map(city => {
            return {
                label: city.toponymName,
                value: city.toponymName,
                lat: city.lat,
                lng: city.lng
            }
        });

    return _cities;
}

export const useGetCites = () => {
    const { showToast } = useCustomToast();
    const { selectedCountry } = useSelector((state) => state.device);

    let { isLoading, data, error, isError } = useQuery(
        ['getCites', { countryName: selectedCountry.value }],
        getCites,
        { enabled: !!selectedCountry?.value }
    );

    if (isError) {
        showToast("error", "Fetching Data", error.response?.data?.message)
    }

    return { isLoading, data };
}

// -------------------------Confirm Device-------------------------//
const confirmDevice = async (payload) => {
    const { data } = await axiosInstance.post("/device/confirm", payload);

    return data.data;
}

export const useConfirmDeviceMutation = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { showToast } = useCustomToast();

    return useMutation(confirmDevice, {
        onSuccess: async (data) => {
            queryClient.invalidateQueries('getUserSubscription');
            queryClient.invalidateQueries('getGroupedDevices');
            dispatch(toggleDialog("confirmDialog"));
            showToast('success', 'Device Confirmation', 'Device Confirmed Successfully!');
        },
        onError: (error) => {
            showToast('error', 'Device Confirmation', error.response?.data?.message);
        }
    })
}

//------------------------------Get Device Info-----------------------------
const getDeviceInfo = async (payload) => {
    const { data } = await axiosInstance.get(`/device/${payload.id}/info`);
    return data.data;
}

export const useGetDeviceInfMutation = () => {
    const { showToast } = useCustomToast();
    const dispatch = useDispatch();

    return useMutation(getDeviceInfo, {
        onSuccess: (data) => {
            const prevCampaign = data?.campaigns[0]?.id;
            dispatch(setSelectedCampaign(prevCampaign));
            dispatch(setSelectedDevice(data));

            // dispatch(toggleDialog("assignCampaignsDropDown"));
        },
        onError: (error) => handleResErrors(error, showToast)
    })
}

//------------------------------Get Device Info-----------------------------
const getDevice = async ({ queryKey }) => {
    const [, id] = queryKey
    const { data } = await axiosInstance.get(`/device/${id}/showinfo`);

    return data.data;
}

export const useGetDevice = (id) => {
    const { showToast } = useCustomToast();

    let { isLoading: groupsLoading, data, error, isError, refetch: refetchGroup } = useQuery(
        ['getDevice', id],
        getDevice,
        {
            // Poll at the same cadence as the Screens list (useGetGroupedDevices)
            // so the detail view's `online` fallback doesn't go stale relative to
            // the list — this used to be fetched once on mount and never refreshed.
            refetchInterval: 15000,
        }
    );

    if (isError) {
        showToast("error", "Fetching Data", error.response?.data?.message)
    }

    return { groupsLoading, data, refetchGroup };

}

//----------------------Remote Access (frp STCP tunnel)----------------------
// Returns the support visitor config + adb/scrcpy connect commands. Flat
// payload (the backend returns response()->json([...]), not a JsonResource),
// so no `.data.data` unwrap here.
const getDeviceRemoteAccess = async ({ queryKey }) => {
    const [, id] = queryKey
    const { data } = await axiosInstance.get(`/device/${id}/remote-access`);

    return data;
}

export const useGetDeviceRemoteAccess = (id) => {
    const { showToast } = useCustomToast();

    const { isLoading, data, error, isError, refetch } = useQuery(
        ['getDeviceRemoteAccess', id],
        getDeviceRemoteAccess,
        { enabled: !!id }
    );

    if (isError) {
        showToast("error", "Remote Access", error.response?.data?.message)
    }

    return { isLoading, data, refetch };
}

//----------------------Remote Access — browser screen mirror (ws-scrcpy)----------------------
// Mints a short-lived (5 min) HMAC-signed URL to the relay's ws-scrcpy gate
// for this device. Called on click (not on page load) so the token is fresh
// when the tab opens. Flat payload: { status, serial, token, url, udid, ... }.
const getDeviceViewScreen = async (id) => {
    const { data } = await axiosInstance.get(`/device/${id}/view-screen`);
    return data;
}

export const useViewScreen = () => {
    const { showToast } = useCustomToast();

    const { mutateAsync, isLoading } = useMutation(getDeviceViewScreen, {
        onError: (error) =>
            showToast("error", "Remote Access", error.response?.data?.message),
    });

    return { requestViewScreen: mutateAsync, isViewScreenLoading: isLoading };
}

// -------------------------Update Device-------------------------//
const updateDevice = async (payload) => {
    const { data } = await axiosInstance.put(`/device/${payload?.id}/update`, payload.data);

    return data.data;
}

export const useUpdateDeviceMutation = () => {
    const { showToast } = useCustomToast();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    return useMutation(updateDevice, {
        onSuccess: async (data) => {
            queryClient.invalidateQueries('getGroupedDevices');
            queryClient.invalidateQueries('getDevice');        // refresh device detail (location/branch)
            queryClient.invalidateQueries('allCompanyDevices');
            dispatch(toggleDialog("updateDevice"));
            dispatch(resetState())
            showToast('success', 'Device Updating', data?.msg);
        },
        onError: (error) => handleResErrors(error, showToast)
    })
}

// -------------------------Flush Device-------------------------//
const flushDevice = async (payload) => {
    const { data } = await axiosInstance.get(`/device/${payload?.id}/screen`);
    return data.data;
}

export const useFlushDevice = () => {
    const { showToast } = useCustomToast();
    const queryClient = useQueryClient();

    return useMutation(flushDevice, {
        onSuccess: async (data) => {
            showToast('success', 'Flush Device', data?.msg);
            queryClient.invalidateQueries('getGroupedDevices');
        },
        onError: (error) => handleResErrors(error, showToast)
    })
}



// -------------------------Restart Device-------------------------//
const restartDevice = async (payload) => {
    const { data } = await axiosInstance.get(`/device/${payload?.id}/restart`);
    return data.data;
}

export const useRestartDevice = () => {
    const { showToast } = useCustomToast();
    const queryClient = useQueryClient();

    return useMutation(restartDevice, {
        onSuccess: async (data) => {
            showToast('success', 'Restart Device', data?.msg);
            queryClient.invalidateQueries('getGroupedDevices');
        },
        onError: (error) => handleResErrors(error, showToast)
    })
}

// -------------------------Reissue Device Token-------------------------//
// Support recovery: the device's local Sanctum token is gone (factory reset,
// reinstall, cache wipe) so it's stuck on 409 from /device/create. Backend
// mints a new token and drops it onto Firebase RTDB at Devices/{serial}/
// newToken; the player's listener swaps it into App.token live (no reboot).
const reissueDeviceToken = async (payload) => {
    const { data } = await axiosInstance.post(`/device/${payload?.id}/reissue-token`);
    return data;
}

export const useReissueDeviceToken = () => {
    const { showToast } = useCustomToast();
    return useMutation(reissueDeviceToken, {
        onSuccess: async (resp) => {
            showToast(
                'success',
                'Reissue Token',
                resp?.message ?? 'Token reissued. The player will pick it up shortly.'
            );
        },
        onError: (error) => handleResErrors(error, showToast),
    })
}

// -------------------------Delete Device-------------------------//
const deleteDevice = async (payload) => {
    const { data } = await axiosInstance.delete(`/device/${payload?.id}`);
    return data.data;
}

export const useDeleteDevice = () => {
    const { showToast } = useCustomToast();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    return useMutation(deleteDevice, {
        onSuccess: async (data) => {
            showToast('success', 'Delete Device', data?.msg);
            queryClient.invalidateQueries('getGroupedDevices');
            queryClient.invalidateQueries('getUserSubscription');
            dispatch(toggleDeleteDialog("device"));
            dispatch(setDisableBtn(false))

        },
        onError: (error) => {
            handleResErrors(error, showToast)
            dispatch(setDisableBtn(false))
        }
    })
}


//------------------------------Action Device-----------------------------
const sendActionDevice = async (payload) => {
    const { data } = await axiosInstance.post(`/device/${payload?.id}/send-action`, payload?.data);
    return data.data;
}

export const useSendActionDevice = () => {
    const { showToast } = useCustomToast();
    const queryClient = useQueryClient();

    return useMutation(sendActionDevice, {
        onSuccess: async (data) => {
            showToast('success', "Device Action", data?.msg);
            queryClient.invalidateQueries('getGroupedDevices');

        },
        onError: (error) => handleResErrors(error, showToast)
    })
}

//------------------------------AI Analytic Device-----------------------------
const updateAiAnaylticDevice = async (payload) => {
    const { data } = await axiosInstance.patch(`/device/${payload?.id}/update/ai_analytic`, payload?.data);
    return data.data;
}

export const useUpdateAiAnaylticDevice = () => {
    const { showToast } = useCustomToast();
    const queryClient = useQueryClient();

    return useMutation(updateAiAnaylticDevice, {
        onSuccess: async (data) => {
            showToast('success', "Device Action", data?.msg);
            queryClient.invalidateQueries('getGroupedDevices');

        },
        onError: (error) => handleResErrors(error, showToast)
    })
}

//------------------------------Mute-Unmute Device-----------------------------
const muteUnmuteDevice = async (payload) => {
    const { data } = await axiosInstance.post(`/device/${payload?.id}/mute`, payload?.data);
    return data.data;
}

export const useMuteUnmuteDevice = () => {
    const { showToast } = useCustomToast();

    return useMutation(muteUnmuteDevice, {
        onSuccess: async (data) => {
            showToast('success', "Mute-Unmute Action", data?.msg);
        },
        onError: (error) => handleResErrors(error, showToast)
    })

}
//------------------------------message Device-----------------------------
const notificationDeviceOff = async (payload) => {
    const { data } = await axiosInstance.post(`/device/message`, payload);
    return data.data;
}

export const useNotificationDeviceOff = () => {
    const { showToast } = useCustomToast();

    return useMutation(notificationDeviceOff, {
        onSuccess: async (data) => {
            showToast('success', "Device Action", data?.msg);
        },
        onError: (error) => handleResErrors(error, showToast)
    })
}

//------------------------------Rotate Device-----------------------------
const rotateDevice = async (payload) => {
    const { data } = await axiosInstance.post(`/device/${payload?.id}/rotate`, payload?.data);
    return data.data;
}

export const useRotateDeviceMutation = () => {
    const { showToast } = useCustomToast();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    return useMutation(rotateDevice, {
        onSuccess: async (data) => {
            dispatch(setEditRotation(false));
            showToast('success', "Rotate Action", data?.msg);
            queryClient.invalidateQueries('getGroupedDevices');
        },
        onError: (error) => handleResErrors(error, showToast)
    })
}


//------------------------------Working Hours Device-----------------------------
const editDeviceSettings = async (payload) => {
    const { data } = await axiosInstance.post(
        `/device/${payload.id}/update/device-settings`,
        payload.data
    );
    return data.data;
};

export const useEditDeviceSettings = () => {
    const { showToast } = useCustomToast();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    return useMutation(editDeviceSettings, {
        onSuccess: (data, variables) => {
            // variables.id is the device id we sent
            queryClient.invalidateQueries(['getDevice', variables.id]);

            showToast('success', 'Device Settings', data?.msg || 'Updated successfully');
            dispatch(toggleDialog("deviceSettings"))
        },
        onError: (error) => handleResErrors(error, showToast),
    });
};

//------------------------------Rotate Device-----------------------------
const updateDeviceGroup = async (payload) => {
    const { data } = await axiosInstance.put(`/device/update/group`, payload?.data);
    return data.data;
}

export const useUpdateDeviceGroup = () => {
    const { showToast } = useCustomToast();

    return useMutation(updateDeviceGroup, {
        onSuccess: async (data) => {
            showToast('success', 'Group Updating', data?.msg);
        },
        onError: (error) => handleResErrors(error, showToast)
    })
}

//------------------------------Test Prayer-----------------------------
const testPrayerDevice = async (payload) => {
    const { data } = await axiosInstance.get(`/device/test/prayer?device_id=${payload?.id}`);
    return data;
}

export const useTestPrayerDevice = () => {
    const { showToast } = useCustomToast();

    return useMutation(testPrayerDevice, {
        onSuccess: async () => {
            showToast('success', "Test Prayer", "Prayer alert sent successfully to the device");
        },
        onError: (error) => handleResErrors(error, showToast)
    })
}
